<template>
  <div class="order-table-wrapper">
    <table
      class="order-table order-table--items"
      aria-labelledby="cart-items-title"
    >
      <caption class="sr-only">
        Artikel in Ihrem Warenkorb
      </caption>
      <OrderTableHeader :has-monthly-price="hasMonthlyColumn" />

      <tbody>
        <OrderTableItem
          v-for="item in cartItemsWithoutBundle"
          :key="item.id"
          :item="item"
          :voucher-message="voucherMessage"
          class="order-table__row order-table__row--border"
        />

        <OrderTableBundle
          v-for="regularBundle in regularBundles"
          :key="regularBundle.bundleOffer.articleNumber"
          :bundle-items="regularBundle.bundleItems"
          :item="regularBundle.bundleOffer"
          :voucher-message="voucherMessage"
          class="order-table__row order-table__row--border"
        />
      </tbody>
    </table>

    <table
      class="order-table order-table--summary"
      aria-labelledby="cart-summary-title"
    >
      <caption class="sr-only">
        Warenkorb Zusammenfassung
      </caption>

      <OrderTableSummaryHeader />

      <tbody>
        <tr
          v-if="isCartEditable"
          class="order-table__row order-table__row--border"
        >
          <td colspan="3">
            <OrderTableCouponForm
              ref="couponForm"
              :disabled="isSubmitting"
              :errors="couponErrors"
              :values="couponValues"
              @submit="addCoupon"
            />
          </td>
        </tr>

        <tr class="order-table__row order-table__price-summary">
          <td
            id="subtotal-label"
            headers="col-description"
          >
            Zwischensumme
          </td>
          <td
            class="order-table__price-value"
            headers="col-amount"
            aria-labelledby="subtotal-label"
          >
            {{ humanizePriceFromCents(subTotal) }}
          </td>
        </tr>

        <tr class="order-table__row order-table__price-summary">
          <td
            id="shipping-label"
            headers="col-description"
          >
            Versandkosten
          </td>
          <td
            class="order-table__price-value"
            headers="col-amount"
            aria-labelledby="shipping-label"
          >
            {{ humanizePriceFromCents(shippingCosts) }}
          </td>
        </tr>

        <tr
          v-if="isCashOnDelivery"
          class="order-table__row order-table__price-summary"
        >
          <td
            id="cashdelivery-label"
            headers="col-description"
          >
            Nachnahmeentgelt (mit dem Gesamtbetrag beim Paketzusteller DHL bezahlen)
          </td>
          <td
            class="order-table__price-value price--highlighted"
            headers="col-amount"
            aria-labelledby="cashdelivery-label"
          >
            6,90 €
          </td>
        </tr>

        <tr
          v-if="voucherInitialSum > 0 || voucherReductionAmount > 0"
          class="order-table__row order-table__price-summary"
        >
          <td
            id="discount-label"
            headers="col-description"
          >
            Rabatt
            <br />
            <span class="order-table__voucher-coupon-amount">
              Restguthaben: {{ humanizePriceFromCents(voucherRestSum) }}
            </span>
          </td>
          <td
            class="order-table__price-value"
            headers="col-amount"
            aria-labelledby="discount-label"
          >
            {{ humanizePriceFromCents(-1 * voucherReductionAmount) }}
          </td>
        </tr>

        <tr
          v-if="paymentTypeRebateName && paymentTypeRebateReductionAmount > 0"
          class="order-table__row order-table__price-summary"
        >
          <td
            id="payment-discount-label"
            headers="col-description"
          >
            {{ paymentTypeRebateName }}
          </td>
          <td
            class="order-table__price-value"
            headers="col-amount"
            aria-labelledby="payment-discount-label"
          >
            {{ humanizePriceFromCents(-1 * paymentTypeRebateReductionAmount) }}
          </td>
        </tr>

        <tr class="order-table__row order-table__price-summary">
          <td
            id="total-label"
            class="text--bold"
            headers="col-description"
          >
            Einmalige Gesamtkosten (inkl. MwSt.)
          </td>
          <td
            class="order-table__price-value text--primary text--bold"
            headers="col-amount"
            aria-labelledby="total-label"
          >
            {{ humanizePriceFromCents(totalPriceWithShipping) }}
          </td>
        </tr>
      </tbody>
    </table>
    <slot name="footer" />

    <FinancingCalculator
      v-if="hasFinancing"
      :price="totalPriceWithShipping"
      visual="cart"
      is-collapsable
    />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { getErrorMessages } from '~/lib/error-parser';
import Dialog from '@/helpers/dialog';
import { humanizePriceFromCents } from '@/lib/price';

import OrderTableItem from '@/components/order-table-item.vue';
import OrderTableBundle from '@/components/order-table-bundle.vue';
import OrderTableCouponForm from '@/components/order-table-coupon-form.vue';
import OrderTableHeader from '@/components/order-table-header.vue';
import OrderTableSummaryHeader from '@/components/order-table-summary-header.vue';
import FinancingCalculator from '@/components/financing-calculator.vue';

export default {
  name: 'OrderTable',
  components: {
    FinancingCalculator,
    OrderTableItem,
    OrderTableCouponForm,
    OrderTableHeader,
    OrderTableSummaryHeader,
    OrderTableBundle,
  },
  props: {
    // Cart is editable on /shop/warenkorb, but not in /shop/zusammenfassung
    isCartEditable: {
      type: Boolean,
      default: true,
    },
  },
  provide() {
    return {
      isCartEditable: this.isCartEditable,
    };
  },
  data() {
    return {
      isSubmitting: false,
      couponErrors: {
        redeemedCoupon: [],
      },
      voucherMessage: {
        success: 'Der Rabatt wird von der Zwischensumme abgezogen.',
        notApplicable: 'Der Rabatt kann auf dieses Produkt nicht angewendet werden.',
      },
    };
  },
  computed: {
    ...mapState({
      bundlesByArticleNumber: (state) => state.cart.bundlesByArticleNumber,
    }),
    ...mapGetters('cart', [
      'cartItems',
      'cartData',
      'subTotal',
      'voucherInitialSum',
      'voucherReductionAmount',
      'voucherRestSum',
      'totalPriceWithShipping',
      'cartItemsWithoutBundle',
      'shippingCosts',
      'redeemedCouponType',
      'redeemedCouponCode',
      'paymentType',
      'possiblePaymentTypes',
      'paymentTypeRebateName',
      'paymentTypeRebateReductionAmount',
      'paymentTypeRebateButtontext',
    ]),
    isCashOnDelivery() {
      return this.paymentType === 'cash_on_delivery';
    },
    totalMonthlyPrice() {
      let price = 0;
      this.cartItems.every((item) => {
        if (item.monthlyPrice) {
          price += item.monthlyPrice * item.quantity;
        }
        return item;
      });
      return price;
    },
    couponValues() {
      return { redeemedCoupon: this.redeemedCouponCode };
    },
    // Hardware Bundles: https://jira.i22.de/browse/LT-8
    regularBundles() {
      const regularBundles = this.cartItems.filter((cartItem) =>
        Object.keys(this.bundlesByArticleNumber).includes(cartItem.articleNumber)
      );
      return regularBundles.map((bundle) => {
        const bundleItems = this.bundlesByArticleNumber[bundle.articleNumber];
        return {
          bundleOffer: bundle,
          bundleItems,
        };
      });
    },
    hasFinancing() {
      return (
        this.isCartEditable && this.possiblePaymentTypes.includes('novalnet_installment_rate_0')
      );
    },
    hasMonthlyColumn() {
      return this.cartItemsWithoutBundle.some((item) => !!item.monthlyPrice);
    },
  },
  methods: {
    humanizePriceFromCents,
    ...mapActions('cart', ['cartAddCoupon']),
    async addCoupon(data) {
      this.isSubmitting = true;
      this.couponErrors = { redeemedCoupon: [] };
      try {
        await this.cartAddCoupon(data);

        const couponType = this.redeemedCouponType === 'voucher' ? 'Gutschein' : 'Coupon';
        await Dialog.info(`Der ${couponType} wurde erfolgreich angewendet.`, { type: 'success' });
      } catch (e) {
        const { errors } = e.data || {};
        const parsed = getErrorMessages(errors);
        if (!parsed.redeemedCoupon) {
          parsed.redeemedCoupon = ['Ein unbekannter Fehler ist aufgetreten.'];
        }
        this.couponErrors = parsed;
        const {
          redeemedCoupon: [message],
        } = this.couponErrors;
        await Dialog.info(message, { type: 'error' });
        const limit =
          errors && errors.redeemedCoupon
            ? errors.redeemedCoupon.find((i) => i.error === 'coupon_limit')
            : null;
        if (limit) {
          await this.cartAddCoupon(data);
        }
      }
      this.isSubmitting = false;
      if (this.couponErrors.redeemedCoupon.length) {
        await this.$nextTick();
        this.focusCouponInput();
      }
    },
    focusCouponInput() {
      this.$refs.couponForm?.focusInput();
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.order-table-wrapper {
  margin-bottom: 2.5 * $grid-base;

  @include mq($mq-sm) {
    margin-bottom: 3 * $grid-base;
  }

  @include mq($mq-md) {
    margin-bottom: 4 * $grid-base;
  }

  @include mq(false, $mq-md - 1) {
    max-width: 600px;
    margin-inline: auto;
  }
}

.order-table {
  width: 100%;
  border-collapse: collapse;

  &__voucher-coupon-amount {
    color: color(grey, 16000);
  }

  &__row {
    padding-top: 1.5 * $grid-base;
    padding-bottom: 1.5 * $grid-base;

    &--border {
      border-bottom: 1px solid color(grey, 25000);
    }
  }

  &__price-summary {
    td {
      padding: $grid-base 0;
    }
  }

  &__price-value {
    text-align: right;
  }
}
</style>
